import type { AppRouter } from "@acme/backend/trpc";
import type { ClientError } from "@acme/backend/utils/clientError";

import { env, isLocalhost, isProduction } from "@acme/env/client";
import { focusManager, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  httpLink,
  loggerLink,
  splitLink,
  TRPCClientError,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { toast } from "sonner";
import superjson from "superjson";

const onTrpcError = ({ error, meta }: { error: unknown; meta: object }) => {
  if (!(error instanceof TRPCClientError)) {
    console.error("QueryCache error: ", error);
    return;
  }

  const clientError = error.data.clientError as ClientError;

  if (!clientError) {
    console.warn("'clientError' not found in server response. Error was TRPCClientError: ", error);
    return;
  }

  if (clientError.identifier === "unauthorized") {
    console.log("Server responded with 'UNAUTHORIZED'. Emitting event 'received-unauthorized-response'.");

    const unauthorizedEvent = new CustomEvent("received-unauthorized-response", {
      detail: {
        meta: {
          time: new Date(),
          ...meta,
        },
      },
    });

    document.dispatchEvent(unauthorizedEvent);

    return;
  }
};

/**
 * Verhindert Refetch-Storm beim Zurückkehren in die PWA (v. a. iOS), wenn die Seite
 * aus dem bfcache wiederhergestellt wird (pageshow.persisted). Ohne dies würde
 * sofort refetchOnWindowFocus ausgelöst und die UX (z. B. offener Verifizierungs-Dialog) leidet.
 */
function setupFocusManagerForPwa(): void {
  if (typeof window === "undefined") return;

  focusManager.setEventListener((handleFocus) => {
    let skipNextFocus = false;

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        skipNextFocus = true;
      }
    };

    const onVisibilityChange = () => {
      if (skipNextFocus) {
        skipNextFocus = false;
        return;
      }
      handleFocus(document.visibilityState === "visible");
    };

    window.addEventListener("pageshow", onPageShow, false);
    window.addEventListener("visibilitychange", onVisibilityChange, false);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("visibilitychange", onVisibilityChange);
    };
  });
}

setupFocusManagerForPwa();

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error, variables, context) => {
        console.error("[QueryClient default mutation options] Mutation error: ", {
          context,
          error,
          variables,
        });
        if (
          error instanceof TRPCClientError &&
          (error.data?.clientError as ClientError | undefined)?.identifier === "fcm-token-invalid"
        ) {
          window.dispatchEvent(new CustomEvent("fcm-token-invalid"));
          return;
        }
        toast.error(error.message);
      },
    },
    queries: {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      retry: isLocalhost ? false : 2,
      staleTime: Number.POSITIVE_INFINITY,
    },
  },
  mutationCache: new MutationCache({
    onError(error, variables, context, mutation) {
      onTrpcError({
        error,
        meta: { context, mutation, type: "mutation", variables },
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      onTrpcError({ error, meta: { query, type: "query" } });
    },
  }),
});

const fetchWithCredentials: typeof fetch = (url, options) =>
  fetch(url, { ...options, credentials: "include" });
const trpcUrl = isLocalhost ? "/api/trpc" : `${env.PUBLIC_BACKEND_URL}/api/trpc`;

const trpcLinks = env.PUBLIC_WEBAPP_IS_REQUEST_BATCHING_DISABLED
  ? [
      httpLink({
        fetch: fetchWithCredentials,
        transformer: superjson,
        url: trpcUrl,
      }),
    ]
  : [
      loggerLink({
        enabled: (opts) => {
          if (env.PUBLIC_WEBAPP_TRPC_LOGGER_IS_DISABLED) {
            return false;
          }

          if (!isProduction) {
            return true;
          }

          return opts.direction === "down" && opts.result instanceof Error;
        },
      }),
      splitLink({
        condition: (op) => op.path === "clanWar.runCwSimulationStream",
        true: httpBatchStreamLink({
          fetch: fetchWithCredentials,
          transformer: superjson,
          url: trpcUrl,
        }),
        false: httpBatchLink({
          fetch: fetchWithCredentials,
          transformer: superjson,
          url: trpcUrl,
        }),
      }),
    ];

export const trpcClient = createTRPCClient<AppRouter>({
  links: trpcLinks,
});

export const api = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
